import { useMemo, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Input, Tabs, Dropdown, Flex, Badge, Typography } from 'antd';
import AllCampaignsTable from './allCampaignsTable';
import { CampaignType } from '@copilot/data/responses/interface';
import {
    displayCampaignOptions,
    getSimpleCampaignNameValidator,
} from '@copilot/common/utils/campaign';
import CampaignTypeDropdown from './campaignTypeDropdown';
import modalManager from '@copilot/common/utils/modalManager';
import { ICampaignStatus } from '@copilot/data/graphql/_generated';
import { CAMPAIGN_LIST_SEARCH_TRACKING_ID } from '@copilot/common/tracking/userpilotEventConsts';
import { applyAllCampaignsFilters } from './allCampaignsPageHelpers';
import { AllCampaignsPageProps } from './allCampaignsPageTypes';
import styles from './allCampaignsPage.module.less';
import {
    ACTIVE_CAMPAIGNS_TAB_KEY,
    INACTIVE_CAMPAIGNS_TAB_KEY,
    PAUSED_CAMPAIGNS_TAB_KEY,
} from './allCampaignsPageConstants';
import { FilterWithSelectedList } from '@copilot/common/components/filters/filterWithSelectedList';
import isNil from 'lodash/isNil';
import { useFeatureFlags } from '@copilot/common/hooks/useFeatureFlags/useFeatureFlags';

/**
 * All Campaign Page presentational component
 * @param props The props.
 */
export function AllCampaignsPage({
    canEditCampaigns,
    showTeamMembers,
    campaignSummaryData,
    isLoading,
    isError,
    campaignFilterType,
    onCampaignFilterChanged,
    teamMembersFilter,
    onTeamMembersFilterChanged,
    onCreateNewCampaign,
    onDeleteCampaign,
    campaignNameSearchTerm,
    onCampaignNameSearchTermChanged,
}: AllCampaignsPageProps) {
    const [tab, setTab] = useState<string>(ACTIVE_CAMPAIGNS_TAB_KEY);
    const { isCampaignLaunchCheckListEnabled } = useFeatureFlags();
    // handle campaign filter type change and campaign data change
    const [activeCampaigns, inactiveCampaigns, unlaunchedCampaigns] = useMemo(() => {
        const activeCampaignData = campaignSummaryData.filter(
            (c) => c.status === ICampaignStatus.Enabled
        );
        const inactiveCampaignData = campaignSummaryData.filter(
            (c) =>
                c.status === ICampaignStatus.Disabled &&
                (!isNil(c.launchedAt) || !isCampaignLaunchCheckListEnabled)
        );
        const unlaunchedCampaignData = campaignSummaryData.filter(
            (c) => c.status === ICampaignStatus.Disabled && isNil(c.launchedAt)
        );
        const selectedTeamMembers = teamMembersFilter.filter((member) => member.isVisible);
        // local filtering of campaign data based on client's selected filters
        const filteredCampaignData = [
            applyAllCampaignsFilters(
                activeCampaignData,
                campaignFilterType,
                selectedTeamMembers,
                campaignNameSearchTerm
            ),
            applyAllCampaignsFilters(
                inactiveCampaignData,
                campaignFilterType,
                selectedTeamMembers,
                campaignNameSearchTerm
            ),
            applyAllCampaignsFilters(
                unlaunchedCampaignData,
                campaignFilterType,
                selectedTeamMembers,
                campaignNameSearchTerm
            ),
        ];

        return filteredCampaignData;
    }, [campaignFilterType, teamMembersFilter, campaignSummaryData, campaignNameSearchTerm]);

    const simpleCampaignNameValidator = getSimpleCampaignNameValidator(campaignSummaryData);

    function showCampaignNameEditor(campaignType: CampaignType) {
        modalManager.openCampaignCreationModal({
            nameEditorValidator: simpleCampaignNameValidator,
            campaignType,
            onCreate: async (name: string) => {
                await onCreateNewCampaign(name, campaignType);
            },
        });
    }

    return (
        <Flex vertical gap={16}>
            <Flex
                vertical
                gap={16}
                justify={'space-between'}
                className={styles.allCampaignsPageHeader}
            >
                <Typography.Title level={4} className={styles.allCampaignsPageHeaderTitle}>
                    Campaigns
                </Typography.Title>
                <Flex justify={'space-between'} align="center">
                    <div className={styles.allCampaignsPageSectionHeaderFilterSection}>
                        <Input.Search
                            placeholder="Search"
                            defaultValue={campaignNameSearchTerm ?? ''}
                            onChange={(event) => {
                                onCampaignNameSearchTermChanged(event.target.value);
                            }}
                            data-tracking-id={CAMPAIGN_LIST_SEARCH_TRACKING_ID}
                        />

                        {showTeamMembers && (
                            <FilterWithSelectedList
                                label="Team Member"
                                filters={teamMembersFilter}
                                onUpdate={onTeamMembersFilterChanged}
                            />
                        )}

                        <CampaignTypeDropdown
                            onChange={(ct) => {
                                onCampaignFilterChanged(ct);
                            }}
                        />
                    </div>

                    {canEditCampaigns && (
                        <Dropdown
                            overlay={displayCampaignOptions(showCampaignNameEditor)}
                            trigger={['click']}
                        >
                            <Button type="primary">
                                <PlusOutlined /> Create Campaign
                            </Button>
                        </Dropdown>
                    )}
                </Flex>
            </Flex>
            <section className={styles.allCampaignsPageSection}>
                <div className={styles.allCampaignsPageTableSectionHeader}>
                    <span>
                        Your connection and reply rates are colour-coded based on internal
                        benchmarks.
                    </span>
                    <span>Last 30 days</span>
                </div>
                <Tabs
                    activeKey={tab}
                    onTabClick={(tabKey) => setTab(tabKey)}
                    className={styles.allCampaignsTabs}
                >
                    <Tabs.TabPane
                        tab={
                            <Flex gap={8}>
                                Active <Badge count={activeCampaigns.length} overflowCount={999} />
                            </Flex>
                        }
                        key={ACTIVE_CAMPAIGNS_TAB_KEY}
                    >
                        <AllCampaignsTable
                            data={activeCampaigns}
                            showTeamMembers={showTeamMembers}
                            campaignNameValidator={simpleCampaignNameValidator}
                            onDeleteCampaign={onDeleteCampaign}
                            canEditCampaigns={canEditCampaigns}
                            isLoading={isLoading}
                            isError={isError}
                        />
                    </Tabs.TabPane>
                    {isCampaignLaunchCheckListEnabled && (
                        <Tabs.TabPane
                            tab={
                                <Flex gap={8}>
                                    Draft{' '}
                                    <Badge count={unlaunchedCampaigns.length} overflowCount={999} />
                                </Flex>
                            }
                            key={INACTIVE_CAMPAIGNS_TAB_KEY}
                        >
                            <AllCampaignsTable
                                data={unlaunchedCampaigns}
                                showTeamMembers={showTeamMembers}
                                campaignNameValidator={simpleCampaignNameValidator}
                                onDeleteCampaign={onDeleteCampaign}
                                canEditCampaigns={canEditCampaigns}
                                isLoading={isLoading}
                                isError={isError}
                            />
                        </Tabs.TabPane>
                    )}
                    <Tabs.TabPane
                        tab={
                            <Flex gap={8}>
                                Inactive{' '}
                                <Badge count={inactiveCampaigns.length} overflowCount={999} />
                            </Flex>
                        }
                        key={PAUSED_CAMPAIGNS_TAB_KEY}
                    >
                        <AllCampaignsTable
                            data={inactiveCampaigns}
                            showTeamMembers={showTeamMembers}
                            campaignNameValidator={simpleCampaignNameValidator}
                            onDeleteCampaign={onDeleteCampaign}
                            canEditCampaigns={canEditCampaigns}
                            isLoading={isLoading}
                            isError={isError}
                        />
                    </Tabs.TabPane>
                </Tabs>
            </section>
        </Flex>
    );
}
